import { ChangeDetectorRef, Component, EventEmitter, Output, ViewChild } from "@angular/core";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { Helper } from "src/app/core/helpers/helper";
import { UsersService } from "../../services/users.service";

@Component({
  selector: "app-otp-modal",
  templateUrl: "./otp-modal.component.html",
  styleUrl: "./otp-modal.component.scss",
  standalone: false
})
export class OtpModalComponent {
  otpModalRef!: NgbModalRef;
  otpModalConfig: any = { size: "md", centered: true, backdrop: "static" };
  isForgotPassword: boolean = false;
  isVerifyOtp: boolean = false;

  otpParameters!: any;
  enteredPhoneOTP = "";
  enteredEmailOTP = "";
  isTimerVisiblePhone = false;
  isTimerVisibleEmail = false;
  timerPhone: number = 30;
  timerEmail: number = 30;
  intervalPhone: any;
  intervalEmail: any;
  isResendPhone = false;
  isResendEmail = false;

  config = {
    allowNumbersOnly: true,
    length: 6,
    isPasswordInput: false,
    disableAutoFocus: false,
    placeholder: "",
    inputStyles: {
      width: "50px",
      height: "50px",
      fontSize: "14px",
      marginRight: "12px"
    }
  };

  @ViewChild("otpModal", { static: true }) otpModal!: NgbModal;

  @Output() otpVerified = new EventEmitter();

  constructor(public helper: Helper, private userService: UsersService, private modalService: NgbModal, private cdr: ChangeDetectorRef) {}

  /**
   * Opens the OTP modal and initializes OTP input fields.
   * @param reqParams The request parameters for OTP generation.
   * @param isVerifyOtp Flag to indicate if OTP verification is required.
   * @param isForgotPassword Flag to indicate if the OTP is for forgot password.
   * @returns {void}
   */
  openModal(reqParams: any, isVerifyOtp: boolean, isForgotPassword: boolean = false): void {
    this.otpParameters = reqParams;
    this.isForgotPassword = isForgotPassword;
    this.isVerifyOtp = isVerifyOtp;

    if (this.otpParameters.sendTo === this.helper.OTPSendMode.SMS_EMAIL) {
      this.config.disableAutoFocus = true;
    }

    setTimeout(() => {
      this.generateOTP(this.otpParameters, true);
    }, 100);
  }

  /**
   * Opens the OTP modal.
   * @returns {void}
   */
  openOtpModal(): void {
    this.otpModalRef = this.modalService.open(this.otpModal, this.otpModalConfig);
    // Handle modal closing and dismissal
    this.otpModalRef?.result.then(
      () => this.closeOtpModal(),
      () => this.closeOtpModal()
    );
  }

  /**
   * Generates an OTP and opens the OTP modal.
   * @param parameters The parameters for OTP generation.
   * @param fromOpenModal Flag to indicate if the method is called from openModal.
   * @returns {void}
   */
  generateOTP(parameters: any, fromOpenModal: boolean = false): void {
    const params = { ...parameters, isShowSuccessToast: false };

    this.userService.generateOTP(params).subscribe((response) => {
      if (response) {
        if (fromOpenModal) {
          this.openOtpModal();
        }
        if (parameters.sendTo === this.helper.OTPSendMode.SMS || parameters.sendTo === this.helper.OTPSendMode.SMS_EMAIL) {
          this.startTimer("phone");
        }
        if (parameters.sendTo === this.helper.OTPSendMode.EMAIL || parameters.sendTo === this.helper.OTPSendMode.SMS_EMAIL) {
          this.startTimer("email");
        }
      }
    });
  }

  /**
   * Handles changes in the phone OTP input field.
   * @param otp The entered phone OTP.
   * @returns {void}
   */
  onOtpChangePhone(otp: string): void {
    this.enteredPhoneOTP = otp;
    this.cdr.detectChanges();
  }

  /**
   * Handles changes in the email OTP input field.
   * @param otp The entered email OTP.
   * @returns {void}
   */
  onOtpChangeEmail(otp: string): void {
    this.enteredEmailOTP = otp;
    this.cdr.detectChanges();
  }

  /**
   * Closes the OTP modal and resets the resend and timer states.
   * @returns {void}
   */
  closeOtpModal(): void {
    this.otpModalRef.close();

    this.isForgotPassword = false;
    this.isVerifyOtp = false;

    this.enteredEmailOTP = "";
    this.enteredPhoneOTP = "";

    // Clear phone OTP related variables and intervals
    this.isResendPhone = false;
    this.isTimerVisiblePhone = false;
    clearInterval(this.intervalPhone);

    // Clear email OTP related variables and intervals
    this.isResendEmail = false;
    this.isTimerVisibleEmail = false;
    clearInterval(this.intervalEmail);
  }

  /**
   * Starts the timer for OTP resend.
   * @param type The type of OTP (phone or email).
   * @returns {void}
   */
  startTimer(type: "phone" | "email"): void {
    if (type === "phone") {
      this.timerPhone = this.helper.secOtpResendInterval;
      this.isTimerVisiblePhone = true;
      this.intervalPhone = setInterval(() => {
        this.timerPhone--;
        if (this.timerPhone === 0) {
          clearInterval(this.intervalPhone);
          this.isTimerVisiblePhone = false;
          this.cdr.detectChanges();
        }
      }, 1000);
    } else if (type === "email") {
      this.timerEmail = this.helper.secOtpResendInterval;
      this.isTimerVisibleEmail = true;
      this.intervalEmail = setInterval(() => {
        this.timerEmail--;
        if (this.timerEmail === 0) {
          clearInterval(this.intervalEmail);
          this.isTimerVisibleEmail = false;
          this.cdr.detectChanges();
        }
      }, 1000);
    }
  }

  /**
   * Resends the phone OTP and restarts the timer.
   * @returns {void}
   */
  resendOtpPhone(): void {
    this.isResendPhone = true;
    this.enteredPhoneOTP = "";
    this.generateOTP({
      sendTo: this.helper.OTPSendMode.SMS,
      countryPhoneCode: this.otpParameters?.countryPhoneCode,
      phone: this.otpParameters?.phone,
      isShowSuccessToast: false
    });
  }

  /**
   * Resends the email OTP and restarts the timer.
   * @returns {void}
   */
  resendOtpEmail(): void {
    this.isResendEmail = true;
    this.enteredEmailOTP = "";
    this.generateOTP({
      sendTo: this.helper.OTPSendMode.EMAIL,
      email: this.otpParameters?.email,
      isShowSuccessToast: false
    });
  }

  /**
   * Verifies the entered OTP and navigates to the reset password page if successful.
   * @returns {void}
   */
  verify(): void {
    const otps: any = {};

    if (this.otpParameters.sendTo === this.helper.OTPSendMode.SMS_EMAIL) {
      if (!this.enteredPhoneOTP || this.enteredPhoneOTP.length !== 6 || !this.enteredEmailOTP || this.enteredEmailOTP.length !== 6) {
        return;
      }
      otps.enteredOTP = this.enteredPhoneOTP;
      otps.enteredOTPMail = this.enteredEmailOTP;
    } else if (this.otpParameters.sendTo === this.helper.OTPSendMode.SMS) {
      if (!this.enteredPhoneOTP || this.enteredPhoneOTP.length !== 6) {
        return;
      }
      otps.enteredOTP = this.enteredPhoneOTP;
    } else if (this.otpParameters.sendTo === this.helper.OTPSendMode.EMAIL) {
      if (!this.enteredEmailOTP || this.enteredEmailOTP.length !== 6) {
        return;
      }
      otps.enteredOTP = this.enteredEmailOTP;
    }

    const params = { ...this.otpParameters, ...otps };

    if (this.isForgotPassword) {
      this.userService.verifyOTP(params).subscribe((response: any) => {
        if (response) {
          const resetData = {
            ...params,
            verificationToken: response.verificationToken
          };
          this.helper.resetPasswordData = resetData;
          this.closeOtpModal();
          this.helper.route.navigateByUrl(`/auth/reset-password`);
        }
      });
    } else if (this.isVerifyOtp) {
      this.userService.verifyOTP(params).subscribe((response: any) => {
        if (response) {
          if (this.isForgotPassword) {
            const resetData = {
              ...params,
              verificationToken: response.verificationToken
            };

            this.helper.resetPasswordData = resetData;
          }

          this.otpVerified.emit(params);
          this.closeOtpModal();
        }
      });
    } else {
      this.otpVerified.emit(params);
    }
  }
}
